import { callAPI } from './apiUtils';
import { apiUrls } from './apiUrls';
import { HTTP_STATUS } from './httpStatusCodes';

/**
 * "Customer details first" for Takeaway / Delivery (Settings > 'Customer details first (takeaway & delivery)', off by default).
 *
 * When the setting is on, the Takeaway / Delivery tab opens with a popup (UpfrontOrderSetupModal) that collects what the usual flow
 * collects AFTER the cart - the service type and time, the customer and, for delivery, the address - and keeps it here for the order
 * being built. Quick Checkout / Order Review's Next then place the order from it and go straight to the payment page.
 *
 * The stored answer lives in sessionStorage (this browser tab / app session), is cleared when the order is placed, and is ignored
 * unless the setting is on (the last answer from the server is cached in localStorage by useUpfrontOrderSetting), so turning the
 * setting off puts the usual flow back at once.
 */

const SETUP_KEY = 'upfront_order_setup_session';
export const UPFRONT_FLAG_KEY = 'upfront_order_setup';

const readSession = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeSession = (key, value) => {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // storage blocked: the answer still applies for as long as the page lives
  }
};

export const isUpfrontEnabledStored = () => {
  try {
    return window.localStorage.getItem(UPFRONT_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

/** The saved setup for the order being built, or null (setting off, nothing saved yet, or unreadable). */
export const getUpfrontSetup = () => {
  if (!isUpfrontEnabledStored()) return null;
  const raw = readSession(SETUP_KEY);
  if (!raw) return null;
  try {
    const setup = JSON.parse(raw);
    return setup && typeof setup === 'object' && setup.service ? setup : null;
  } catch {
    return null;
  }
};

export const saveUpfrontSetup = (setup) => {
  writeSession(SETUP_KEY, JSON.stringify(setup));
};

export const clearUpfrontSetup = () => {
  writeSession(SETUP_KEY, null);
};

/** One line for the cart: "Takeaway · Today 6:30pm · Gagan Singh". */
export const describeUpfrontSetup = (setup) => {
  if (!setup) return '';
  const service = setup.service === 'delivery' ? 'Delivery' : 'Takeaway';
  const dayLabel = setup.day === 'today' ? 'Today' : setup.day === 'tomorrow' ? 'Tomorrow' : setup.date || setup.day || '';
  const when = [dayLabel, setup.time].filter(Boolean).join(' ');
  const who = setup.customer ? setup.customer.name || 'Customer' : 'Guest';
  return [service, when, who].filter(Boolean).join(' · ');
};

const fail = (message) => ({ ok: false, message });
const failCreated = () => ({
  ok: false,
  orderCreated: true,
  message: 'The order was placed but could not be opened for payment. Find it under All Orders.',
});

/**
 * Places the order from the saved setup with the same calls, in the same order, as the usual flow:
 *   ORDER_CREATE (Order Review's Next)  ->  GET_ORDER_INFO + UPDATE_ORDER_CUSTOMER (Select Customer)
 *   ->  SET_ORDER_ADDRESS (Delivery Address, delivery only)  ->  the payment page.
 * Returns { ok: true, orderId, customerId, warning? } (orderId is the numeric id the payment page takes) or { ok: false, message,
 * orderCreated? }. Once the order exists it is never thrown away: if the customer or the address cannot be attached, the order still
 * goes to the payment page (ok: true) with a `warning` to show, where both can be fixed; a retry from the cart would find it empty.
 * `orderCreated` marks the one failure after creation that cannot continue (the new order could not be loaded).
 * Nothing is cleared here: the caller clears the setup once it has navigated.
 */
export async function placeUpfrontOrder({ setup, serverId = null, sessionId }) {
  if (!setup || !setup.service) return fail('Choose the service type first.');
  const delivery = setup.service === 'delivery';
  if (delivery && !setup.customer) return fail('A delivery order needs a customer.');
  if (delivery && !setup.address) return fail('A delivery order needs a delivery address.');

  const payload = {
    server_id: serverId,
    checkout_type: 'phone-order',
    preferred_order_day: setup.day === 'other' ? setup.date : setup.day,
    preferred_order_time: setup.time || '',
    order_type: delivery ? 'delivery' : 'take_away',
    sub_order_type: setup.service,
    service_type: '',
    order_name: '',
    cust_mobile: '',
    cust_name: '',
    session_id: sessionId,
  };

  let created;
  try {
    created = await callAPI(apiUrls.ORDER_CREATE, {}, 'POST', payload, {});
  } catch (e) {
    return fail('Could not place the order. Try again.');
  }
  if (created?.data?.status !== 1) return fail(created?.data?.message || 'Could not place the order.');

  const orderNumber = created.data?.data?.order_id;
  if (!orderNumber) return fail('The order was created but its number was not returned.');
  try {
    window.localStorage.setItem('current_order_id', orderNumber);
  } catch {
    // not needed by this flow: the ids below are passed on directly
  }

  let numericId;
  let orderRef;
  try {
    const info = await callAPI(apiUrls.GET_ORDER_INFO, { order_id: orderNumber }, 'GET');
    if (info?.status !== HTTP_STATUS.OK || info?.data?.status !== 1) return failCreated();
    numericId = info.data.orderDetail?.id;
    orderRef = info.data.orderNumber;
  } catch (e) {
    return failCreated();
  }
  if (!numericId) return failCreated();

  let customerId = null;
  let warning = '';
  if (setup.customer?.id) {
    try {
      const attached = await callAPI(apiUrls.UPDATE_ORDER_CUSTOMER, {}, 'POST', { order_numbers: orderRef, customer: setup.customer.id }, {});
      if (attached?.status === HTTP_STATUS.OK) {
        customerId = setup.customer.id;
        try {
          window.localStorage.setItem('current_customer_id', customerId);
        } catch {
          // see above
        }
      } else {
        warning = 'The customer could not be added to the order. Add them on the payment page.';
      }
    } catch (e) {
      warning = 'The customer could not be added to the order. Add them on the payment page.';
    }
  }

  if (delivery) {
    try {
      const addressSet = await callAPI(apiUrls.SET_ORDER_ADDRESS, {}, 'POST', { address_id: setup.address.id, order_id: numericId }, {});
      if (addressSet?.status !== HTTP_STATUS.OK) warning = warning || 'The delivery address could not be set. Set it on the payment page.';
    } catch (e) {
      warning = warning || 'The delivery address could not be set. Set it on the payment page.';
    }
  }

  return warning ? { ok: true, orderId: numericId, customerId, warning } : { ok: true, orderId: numericId, customerId };
}
